import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {RichText} from '@payloadcms/richtext-lexical/react';

/**
 * Props for the CopyrightBlock component.
 */
interface ComponentProps {
    /** The Lexical rich text content with copyright notices / image credits. */
    content: any;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'CopyrightBlock'
     */
    testId?: string;
    /** Optional heading, e.g. "Image credits". */
    title?: string | null;
}

/**
 * Renders copyright notices / image credits in a small, muted style. The block is display-only
 * and is intentionally not part of the landingpage audio.
 *
 * @param props         The component props.
 * @param props.content The Lexical serialized rich text data.
 * @param props.testId  An optional test ID. Defaults to `'CopyrightBlock'`.
 * @param props.title   An optional heading.
 * @returns A JSX element rendering the copyright notices.
 *
 * @example
 * ```tsx
 * <CopyrightBlock content={block.content} title={block.title} />
 * ```
 */
const CopyrightBlock = ({content, testId = 'CopyrightBlock', title}: ComponentProps) => {
    if (!content) return null;

    return (
        <Wrapper aria-label={title ?? undefined} data-cy={testId}>
            {title && <Title>{title}</Title>}
            <RichText data={content} />
        </Wrapper>
    );
};

CopyrightBlock.displayName = 'CopyrightBlock';

export {CopyrightBlock};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.aside`
    border-top: 1px solid ${({theme}) => theme.colors.inkDisabled};
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    line-height: 1.5;
    max-width: 78rem;
    padding: ${spacing(4)} 0 0;

    p {
        margin: 0 0 ${spacing(1)};
    }

    ul {
        margin: 0;
        padding-left: ${spacing(4)};
    }

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const Title = styled.h2`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0 0 ${spacing(2)};
`;